/**
 * Turn a caught API error into a short, human-readable string for a toast.
 *
 * The backend's structured validation errors (`code: 'VALIDATION_ERROR'`,
 * `errors: [{field, message}]`) carry the field name that's actually wrong —
 * but every catch block across the app was showing `data.message` alone,
 * which for a Zod error is just the FIRST issue's raw text ("Required",
 * "Expected string, received null") with NO indication of which field. That
 * reads as gibberish no matter how it's formatted. This resolves the field
 * name + lightly rephrases the common raw Zod messages, so the user sees
 * e.g. "Scheduled for is required" instead of a bare "Required".
 *
 * i18n (spec 91 R3): field labels + rephrased Zod reasons are resolved via the
 * i18n SINGLETON — `i18n.t('common:apiFieldError.…', { defaultValue })` —
 * because this is called from catch blocks both inside and OUTSIDE React render.
 * `defaultValue` is byte-identical to the historical English so `en` output is
 * unchanged; the field-name/Zod-message matching + assembly logic is untouched.
 */

import i18n from '../i18n';

/** Human labels for field names seen in inspection/template/category/recurring-
 *  schedule API errors. Falls back to a humanized camelCase conversion for
 *  anything not listed here — this list doesn't need to be exhaustive.
 *  (English defaults; localized in `humanizeField` via the singleton.) */
const FIELD_LABELS: Record<string, string> = {
  propertyId: 'Property',
  unitId: 'Unit',
  type: 'Type / category',
  customCategoryId: 'Category',
  name: 'Name',
  scheduledFor: 'Scheduled for',
  inspectorId: 'Inspector',
  templateId: 'Template',
  notes: 'Notes',
  groups: 'Checklist sections',
  scopeLevel: 'Scope',
  tier: 'Audience',
  isDefault: 'Default template',
  cadence: 'Cadence',
  obligationId: 'Compliance obligation',
  leadDays: 'Lead days',
  defaultInspectorId: 'Default inspector',
  responsibleVendorId: 'Responsible vendor',
  nextDueDate: 'Next due date',
  firstInspectionDate: 'First inspection date',
  endDate: 'End date',
};

function humanizeField(field: string): string {
  const known = FIELD_LABELS[field];
  if (known) return i18n.t(`common:apiFieldError.field.${field}`, { defaultValue: known });
  const last = field.split('.').pop() ?? field;
  const spaced = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Light rephrasing of the most common raw Zod messages. Anything else
 *  (e.g. a custom `.refine()` message) passes through unchanged — those are
 *  already written to be read by a human. */
function humanizeZodMessage(message: string): string {
  if (message === 'Required')
    return i18n.t('common:apiFieldError.reason.required', { defaultValue: 'is required' });
  if (/^Expected .+, received null$/i.test(message))
    return i18n.t('common:apiFieldError.reason.blank', { defaultValue: 'cannot be left blank' });
  if (/^Expected .+, received undefined$/i.test(message))
    return i18n.t('common:apiFieldError.reason.required', { defaultValue: 'is required' });
  if (/^Invalid uuid$/i.test(message))
    return i18n.t('common:apiFieldError.reason.invalidSelection', {
      defaultValue: 'is not a valid selection',
    });
  if (/^Invalid email$/i.test(message))
    return i18n.t('common:apiFieldError.reason.invalidEmail', {
      defaultValue: 'is not a valid email address',
    });
  if (/^Invalid date$/i.test(message) || /^Invalid datetime$/i.test(message))
    return i18n.t('common:apiFieldError.reason.invalidDate', {
      defaultValue: 'is not a valid date',
    });
  if (/^Invalid /i.test(message))
    return i18n.t('common:apiFieldError.reason.invalid', { defaultValue: 'is invalid' });
  return message;
}

interface ApiValidationIssue {
  field?: string;
  message?: string;
}

interface ApiErrorData {
  message?: string;
  code?: string;
  errors?: ApiValidationIssue[];
  error?: { code?: string; message?: string };
}

/** Cap how many field errors show in one toast — a huge malformed payload
 *  shouldn't produce a wall of text. */
const MAX_FIELD_ERRORS = 5;

/**
 * Extract a readable message from a caught axios error. Field-level
 * VALIDATION_ERROR responses become "Field is required; Other field is
 * invalid" (never the bare Zod message alone); anything else falls back to
 * the server's plain message, else the given fallback.
 */
export function extractApiErrorMessage(err: unknown, fallback?: string): string {
  // Localize the DEFAULT fallback only — a caller-supplied `fallback` is used
  // verbatim (callers pass their own already-localized copy). Byte-identical
  // English default keeps `en` unchanged.
  const resolvedFallback =
    fallback ??
    i18n.t('common:apiFieldError.genericFallback', {
      defaultValue: 'Something went wrong — please try again.',
    });

  const axiosErr = err as { response?: { data?: ApiErrorData } };
  const data = axiosErr?.response?.data;
  if (!data) return resolvedFallback;

  if (data.code === 'VALIDATION_ERROR' && Array.isArray(data.errors) && data.errors.length > 0) {
    const shown = data.errors.slice(0, MAX_FIELD_ERRORS);
    const lines = shown.map((e) => {
      const field = humanizeField(e.field || 'Value');
      const reason = humanizeZodMessage(
        e.message || i18n.t('common:apiFieldError.reason.invalid', { defaultValue: 'is invalid' }),
      );
      return `${field} ${reason}`;
    });
    const remaining = data.errors.length - shown.length;
    return (
      lines.join('; ') +
      (remaining > 0
        ? ' ' + i18n.t('common:apiFieldError.more', { n: remaining, defaultValue: '(+{{n}} more)' })
        : '')
    );
  }

  return data.message || data.error?.message || resolvedFallback;
}
